// LIBRERIE/IMPORTS
import React from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";

// HOOKS
import { useGetAgentsByAgency } from "../../hooks/useGetAgents";
import { useGetAgencyDetails } from "../../hooks/useGetAgencyDetails";

// COMPONENTI
import SubHeaderDefault from "../Header/subHeader/SubHeaderDefault";
import AgentsSection from "./AgentsSection";
import ServerError from "../Error/ServerError";
import LoadingIndicator from "../Loaders/LoadingIndicator";
import WidgetContacts from "../Widgets/WidgetContacts/WidgetContacts";
import WidgetPartners from "../Widgets/WidgetPartners/WidgetPartners";

// MODULI/FOGLI DI STILE
import styles from './AboutUs.module.css';

// CONFIGURAZIONI
import appConfig from "../../config/appConfig";

interface AboutUsProps {
    agencyId: number;
    showAgents: boolean;
}

const AboutUs: React.FC<AboutUsProps> = ({ agencyId, showAgents }) => {
    const { t } = useTranslation();
    const { agents, loading: agentsLoading, error: agentsError } = useGetAgentsByAgency(agencyId);
    const { agency, loading: agencyLoading, error: agencyError } = useGetAgencyDetails(agencyId);

    const aboutUsImages = [
        { src: '/assets/images/aboutus/image-1.jpg', alt: t('aboutUsPage.images.alt1') },
        { src: '/assets/images/aboutus/image-2.jpg', alt: t('aboutUsPage.images.alt2') },
        { src: '/assets/images/aboutus/image-3.jpg', alt: t('aboutUsPage.images.alt3') },
        { src: '/assets/images/aboutus/image-4.jpg', alt: t('aboutUsPage.images.alt4') },
        { src: '/assets/images/aboutus/image-5.jpg', alt: t('aboutUsPage.images.alt5') },
    ];

    const allPartnersForWidget = appConfig.content.partners;

    if (agencyLoading) return <LoadingIndicator />;
    if (agencyError) return <ServerError />;
    if (agentsLoading && showAgents) return <LoadingIndicator />;
    if (agentsError && showAgents) console.error("Error fetching agents:", agentsError);

    return (
        <>
            <Helmet>
                <title>{t('aboutUsPage.pageTitle')} | {agency?.agencyName || 'Agenzia Immobiliare'}</title>
                <link rel="preconnect" href="https://fonts.googleapis.com" />
                <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
                <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400&family=Lato:wght@300;400&display=swap" rel="stylesheet" />
            </Helmet>

            <SubHeaderDefault title={t('aboutUsPage.pageTitle')} isSeo={true} />

            <section className="space-ptb">
                <div className="container-fluid">
                    {/* Intestazione della pagina */}
                    <div className="row justify-content-center mb-5">
                        <div className="col-lg-9 text-center">
                            <h1 className="text-primary mb-4 display-5">{t('aboutUsPage.sectionHeading')}</h1>
                            <p className="px-sm-5 mb-4 lead">{t('aboutUsPage.sectionSubHeading')}</p>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-lg-8 mb-5 mb-lg-0">
                            <div className={styles.aboutUsContent}>

                                {/* --- LA NOSTRA STORIA --- */}
                                <h3>{t('aboutUsPage.storyTitle')}</h3>

                                {aboutUsImages[0] && (
                                    <img
                                        src={aboutUsImages[0].src}
                                        alt={aboutUsImages[0].alt}
                                        className={`${styles.aboutImage} ${styles.imageFloatRight}`}
                                    />
                                )}
                                <p className={`${styles.firstParagraph} mb-4`}>{t('aboutUsPage.storyP1')}</p>



                                <p className="mb-4">{t('aboutUsPage.storyP2')}</p>
                                <p>{t('aboutUsPage.storyP3')}</p>




                                {/* --- PROFESSIONALITÀ, AFFIDABILITA’ E RISERVATEZZA --- */}
                                <h3>{t('aboutUsPage.professionalismTitle')}</h3>
                                <p className="mb-4">{t('aboutUsPage.professionalismP1')}</p>
                                <p>{t('aboutUsPage.professionalismP2')}</p>

                                {aboutUsImages[1] && (
                                    <img
                                        src={aboutUsImages[1].src}
                                        alt={aboutUsImages[1].alt}
                                        className={styles.aboutImage}
                                    />
                                )}

                                <hr className={styles.sectionDivider} />

                                {/* --- MISSION --- */}
                                <h3>{t('aboutUsPage.missionTitle')}</h3>
                                <p className={`${styles.firstParagraph} mb-4`}>{t('aboutUsPage.missionP1')}</p>
                                <p>{t('aboutUsPage.missionP2')}</p>
                                <p>{t('aboutUsPage.missionP3')}</p>
                                <p>{t('aboutUsPage.missionP4')}</p>
                                <p>{t('aboutUsPage.missionP5')}</p>
                                <p>{t('aboutUsPage.missionP6')}</p>
                                <p>{t('aboutUsPage.missionP7')}</p>

                                <hr className={styles.sectionDivider} />

                                {aboutUsImages[2] && (
                                    <img
                                        src={aboutUsImages[2].src}
                                        alt={aboutUsImages[2].alt}
                                        className={styles.aboutImage}
                                    />
                                )}

                                {/* --- VISION --- */}
                                <h3>{t('aboutUsPage.visionTitle')}</h3>
                                <p className={`${styles.firstParagraph} mb-4`}>{t('aboutUsPage.visionP1')}</p>
                                <p>{t('aboutUsPage.visionP2')}</p>
                                <p>{t('aboutUsPage.visionP3')}</p>
                                <p>{t('aboutUsPage.visionP4')}</p>

                                <hr className={styles.sectionDivider} />

                                {aboutUsImages[3] && (
                                    <img
                                        src={aboutUsImages[3].src}
                                        alt={aboutUsImages[3].alt}
                                        className={styles.aboutImage}
                                    />
                                )}

                                {/* --- QUALCHE INFORMAZIONE SU DI ME --- */}
                                <h3>{t('aboutUsPage.personalInfo.title')}</h3>
                                <p className="lead"><strong>{t('aboutUsPage.personalInfo.subTitle')}</strong></p>
                                <blockquote className={`${styles.quoteBlock} mb-4`}>
                                    <p className="mb-0">{t('aboutUsPage.personalInfo.quote.text')}</p>
                                    <footer className={styles.quoteFooter}>{t('aboutUsPage.personalInfo.quote.author')}</footer>
                                </blockquote>

                                {aboutUsImages[4] && (
                                    <img
                                        src={aboutUsImages[4].src}
                                        alt={aboutUsImages[4].alt}
                                        className={styles.aboutImage}
                                    />
                                )}

                                <p>{t('aboutUsPage.personalInfo.quote.reflection1')}</p>
                                <p>{t('aboutUsPage.personalInfo.quote.reflection2')}</p>
                                <p>{t('aboutUsPage.personalInfo.quote.reflection3')}</p>

                                {/* Chi sono */}
                                <h4 className="mt-4">{t('aboutUsPage.personalInfo.whoAmI.title')}</h4>
                                <p className="mb-4">{t('aboutUsPage.personalInfo.whoAmI.p1')}</p>
                                <p className="mb-4">{t('aboutUsPage.personalInfo.whoAmI.p2')}</p>
                                <p>{t('aboutUsPage.personalInfo.whoAmI.p3')}</p>

                                {/* Il salto nel mondo immobiliare */}
                                <h4 className="mt-4">{t('aboutUsPage.personalInfo.theJump.title')}</h4>
                                <p>{t('aboutUsPage.personalInfo.theJump.p1')}</p>
                                <p>{t('aboutUsPage.personalInfo.theJump.p2')}</p>

                                {/* Cosa posso fare per te */}
                                <h4 className="mt-4">{t('aboutUsPage.personalInfo.whatIDo.title')}</h4>
                                <p className={`${styles.listIntro}`}>{t('aboutUsPage.personalInfo.whatIDo.intro')}</p>
                                <ul className={`${styles.serviceList} mb-4`}>
                                    <li><i className="bi bi-check-lg"></i>{t('aboutUsPage.personalInfo.whatIDo.item1')}</li>
                                    <li><i className="bi bi-check-lg"></i>{t('aboutUsPage.personalInfo.whatIDo.item2')}</li>
                                    <li><i className="bi bi-check-lg"></i>{t('aboutUsPage.personalInfo.whatIDo.item3')}</li>
                                    <li><i className="bi bi-check-lg"></i>{t('aboutUsPage.personalInfo.whatIDo.item4')}</li>
                                    <li><i className="bi bi-check-lg"></i>{t('aboutUsPage.personalInfo.whatIDo.item5')}</li>
                                    <li><i className="bi bi-check-lg"></i>{t('aboutUsPage.personalInfo.whatIDo.item6')}</li>
                                    <li><i className="bi bi-check-lg"></i>{t('aboutUsPage.personalInfo.whatIDo.item7')}</li>
                                </ul>

                                {/* Perché scegliere me */}
                                <h4 className="mt-4">{t('aboutUsPage.personalInfo.whyChooseMe.title')}</h4>
                                <p>{t('aboutUsPage.personalInfo.whyChooseMe.question')}</p>
                                <p>{t('aboutUsPage.personalInfo.whyChooseMe.usp1')}</p>
                                <p>{t('aboutUsPage.personalInfo.whyChooseMe.usp2')}</p>
                                <p>{t('aboutUsPage.personalInfo.whyChooseMe.focus1')}</p>
                                <p>{t('aboutUsPage.personalInfo.whyChooseMe.focus2')}</p>
                                <p>{t('aboutUsPage.personalInfo.whyChooseMe.satisfaction1')}</p>
                                <p>{t('aboutUsPage.personalInfo.whyChooseMe.satisfaction2')}</p>

                                {/* Call to Action Finale */}
                                <div className={styles.finalCta}>
                                    <p>{t('aboutUsPage.personalInfo.callToAction.p1')}</p>
                                    <p>{t('aboutUsPage.personalInfo.callToAction.p2')}</p>
                                </div>

                            </div>
                        </div>

                        {/* Colonna Sidebar (Widgets) */}
                        <div className="col-lg-4 mt-4 mt-lg-0">
                            <div className="sidebar">
                                <WidgetContacts
                                    agencyDataAddress={agency?.address_details?.address ?? ''}
                                    agencyDataCity={agency?.address_details?.city_name ?? ''}
                                    agencyDataComuneCapoluogo={agency?.address_details?.city_name === agency?.address_details?.province_name}
                                    agencyDataProvinciaSigla={agency?.address_details?.sigla ?? ''}
                                    agencyDataEmail={appConfig.agency.agencyEmail}
                                    agencyDataEmailShow={appConfig.ui.agencyWidget.showEmail}
                                    agencyDataPhone1={agency?.phone1 ?? ''}
                                    agencyDataPhone2={agency?.phone2 ?? ''}
                                    agencyDataInternationalPrefix={appConfig.agency.intlPrefix ?? '+39'}
                                    agencyDataMobile={agency?.mobile ?? ''}
                                    agencyDataMobile2={agency?.mobile2 ?? ''}
                                    agencyDataFax={agency?.fax ?? ''}
                                    agencySocialShow={appConfig.ui.agencyWidget.showSocialLinks}
                                    agencyDataWhatsapp={appConfig.socialLinks.whatsapp}
                                    agencyDataFacebook={appConfig.socialLinks.facebook}
                                    agencyDataInstagram={appConfig.socialLinks.instagram}
                                    agencyDataTwitter={appConfig.socialLinks.twitter}
                                    agencyDataLinkedin={appConfig.socialLinks.linkedin}
                                    agencyDataPinterest={appConfig.socialLinks.pinterest}
                                    agencyDataYoutube={appConfig.socialLinks.youtube}
                                />
                                <WidgetPartners partners={allPartnersForWidget} />
                            </div>
                        </div>
                    </div>

                    <AgentsSection
                        showAgents={showAgents}
                        agents={agents}
                        agentsLoading={agentsLoading}
                        agentsError={agentsError}
                    />

                </div>
            </section>
        </>
    );
};
export default AboutUs;